import { _isNotEmpty } from '@/littledash';
import type { SendCoverageGridRow, SendVerdictTally } from '@/model/Agent.model';
import { Fragment, type FC } from 'react';
import { definitionSourceText, tierText } from '@/components/Agents/SendValidator/utils/SendTiers';
import styles from './SendResults.module.scss';

/** A tier row as stored: one stored before tiers were split by source has no sources. */
interface StoredTierRow extends SendCoverageGridRow {
  by_definition_source?: Array<SendCoverageGridRow>;
}

interface SendTierGridProps {
  rows: ReadonlyArray<StoredTierRow>;
  /** The published rules the tiers divide: the grid's own denominator. */
  registerRules: number;
  /** Our data-quality checks, counted apart and never against the published rules. */
  tierEightChecks?: number;
  testId?: string;
}

const TallyCells: FC<{ tally: SendVerdictTally }> = ({ tally }) => (
  <>
    <td data-test-element="tier-passed">
      {tally.passed}
      {/* As the tally tiles do: a pass short of an exact, finished check is never read as one. */}
      {tally.passed > tally.conforming && (
        <span className={styles.cellNote}>{tally.passed - tally.conforming} in part</span>
      )}
    </td>
    <td>{tally.failed}</td>
    <td>{tally.not_applicable}</td>
    <td>{tally.not_evaluated}</td>
    <td data-test-element="tier-total">{tally.total}</td>
  </>
);

/**
 * How the published rules landed for each tier, tier 0 to 7, out of the register's own total, each split by
 * where its rules' checks come from: CDISC's published encoding, our check derived from CDISC's prose, or no
 * possible check. So a derived rule is never counted as CDISC's own. Tier 8, data quality, is ours alone and
 * appears only in the line beneath, never against the published total. Every cell is the server's number.
 */
const SendTierGrid: FC<SendTierGridProps> = ({
  rows,
  registerRules,
  tierEightChecks,
  testId = 'invivo-send-validator__tier-grid',
}) => {
  if (!_isNotEmpty(rows)) {
    return null;
  }
  return (
    <div className={`${styles.gridScroll} mt3`} data-test-component="SendTierGrid" data-testid={testId}>
      <table className={styles.grid}>
        <caption className={styles.gridCaption} data-test-element="tier-grid-caption">
          Published rules by tier, out of {registerRules}
        </caption>
        <thead>
          <tr>
            <th scope="col">Tier</th>
            <th scope="col">Passed</th>
            <th scope="col">Failed</th>
            <th scope="col">Not applicable</th>
            <th scope="col">Not evaluated</th>
            <th scope="col">Rules</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Fragment key={row.key}>
              <tr data-test-element="tier-row" data-test-key={row.key}>
                <th scope="row">{tierText(row.key)}</th>
                <TallyCells tally={row.tally} />
              </tr>
              {(row.by_definition_source ?? []).map((source) => (
                <tr
                  key={`${row.key}-${source.key}`}
                  className={styles.tierSourceRow}
                  data-test-element="tier-source-row"
                  data-test-key={`${row.key}-${source.key}`}
                >
                  <th scope="row">{definitionSourceText(source.key)}</th>
                  <TallyCells tally={source.tally} />
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      {tierEightChecks !== undefined && (
        <p className="f6 dark-gray ma0 mt2" data-test-element="tier-eight">
          {tierText('tier_8')}: {tierEightChecks} of our own checks, counted apart and never against the {registerRules}{' '}
          published rules.
        </p>
      )}
    </div>
  );
};

export default SendTierGrid;
